import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import useAxiosAuth from '../hooks/useAxiosAuth';
import baseURL from '../utils/baseURL';

// CARE PROGRAMMES (doc/care-programmes-plan.md, Round B) — the tenant web's
// half of `/api/care-programmes` (the templates a doctor or the hospital
// builds) and `/api/patient-programmes` (a programme started for a patient,
// with the timeline the server computes on every read).
//
// A PROGRAMME is a doctor's or the hospital's own definition — a name, what
// the start date is called, how long it runs and its milestones (offset in
// days, a ± window, optionally a check set whose reading proves it). Nothing
// is shipped pre-made. A PATIENT PROGRAMME is one started for one person on a
// start date; the server snapshots the template and answers every read with
// the timeline (week, expected end, each milestone done / due / late /
// upcoming and what proved it). This slice never computes a timeline.
//
//   GET    /care-programmes?purpose=manage|start&doctorId=   → { purpose, doctorId, mine, hospital }
//   POST   /care-programmes                                  → 201 { programme }
//   PUT    /care-programmes/:programmeId                     → { programme }
//   DELETE /care-programmes/:programmeId                     → { programme } (soft)
//   POST   /care-programmes/:programmeId/copy                → 201 { programme, copiedFrom }
//   GET    /patient-programmes/patient/:patientId?status=    → { patientId, scope, active, closed }
//   POST   /patient-programmes                               → 201 { programme }
//   PUT    /patient-programmes/:id/milestones/:key           → { programme }
//   POST   /patient-programmes/:id/close                     → { programme, lifeEventEntryId }
//   GET    /patient-programmes/booking-line?patientId=&doctorId= → { programmes: [Line] }
//   GET    /check-sets?purpose=manage&doctorId=              → the sets a milestone may attach
//
// Every refusal keeps the server's `code`, `index` (a position in
// `milestones`) and `field`, so a form can put the server's own sentence next
// to the input it names.

const serializeError = (error) => {
    const data = error?.response?.data;
    if (data && typeof data === 'object') {
        return {
            code: data.code || null,
            message: data.message || null,
            status: error.response?.status || null,
            index: typeof data.index === 'number' ? data.index : null,
            field: data.field ?? null,
            patientProgrammeId: data.patientProgrammeId ?? null,
            permission: data.permission || null,
            feature: data.feature || null,
        };
    }
    return { code: null, message: error?.message || 'Request failed', status: error?.response?.status || null };
};

const byName = (a, b) => String(a?.name || '').localeCompare(String(b?.name || ''));
const enc = encodeURIComponent;

/* ---------------------------------------------------------------------------
 * Templates
 * ------------------------------------------------------------------------- */

export const fetchProgrammes = createAsyncThunk(
    'programmes/fetch',
    async ({ doctorId = null } = {}, { rejectWithValue }) => {
        const axiosInstance = useAxiosAuth();
        try {
            const params = new URLSearchParams({ purpose: 'manage' });
            if (doctorId) params.set('doctorId', doctorId);
            const res = await axiosInstance.get(`${baseURL}/care-programmes?${params.toString()}`);
            return res.data || {};
        } catch (error) {
            return rejectWithValue(serializeError(error));
        }
    }
);

export const createProgramme = createAsyncThunk(
    'programmes/create',
    async ({ body }, { rejectWithValue }) => {
        const axiosInstance = useAxiosAuth();
        try {
            const res = await axiosInstance.post(`${baseURL}/care-programmes`, body);
            return res.data || {};
        } catch (error) {
            return rejectWithValue(serializeError(error));
        }
    }
);

export const updateProgramme = createAsyncThunk(
    'programmes/update',
    async ({ programmeId, changes }, { rejectWithValue }) => {
        const axiosInstance = useAxiosAuth();
        try {
            const res = await axiosInstance.put(`${baseURL}/care-programmes/${enc(programmeId)}`, changes);
            return res.data || {};
        } catch (error) {
            return rejectWithValue(serializeError(error));
        }
    }
);

export const deleteProgramme = createAsyncThunk(
    'programmes/delete',
    async ({ programmeId }, { rejectWithValue }) => {
        const axiosInstance = useAxiosAuth();
        try {
            const res = await axiosInstance.delete(`${baseURL}/care-programmes/${enc(programmeId)}`);
            return { programmeId, programme: res.data?.programme || null };
        } catch (error) {
            return rejectWithValue(serializeError(error));
        }
    }
);

export const copyProgramme = createAsyncThunk(
    'programmes/copy',
    async ({ programmeId, doctorId, name }, { rejectWithValue }) => {
        const axiosInstance = useAxiosAuth();
        try {
            const body = {};
            if (doctorId) body.doctorId = doctorId;
            if (name) body.name = name;
            const res = await axiosInstance.post(`${baseURL}/care-programmes/${enc(programmeId)}/copy`, body);
            return res.data || {};
        } catch (error) {
            return rejectWithValue(serializeError(error));
        }
    }
);

// The check sets a milestone may attach (contract "setId"): a doctor's
// programme → that doctor's own sets + the hospital's; a hospital programme →
// the hospital's only. Read from the Round A list, kept HERE (not in
// checkSetSlice.manage, which holds the Check sets screen's own list).
export const fetchProgrammeSetOptions = createAsyncThunk(
    'programmes/fetchSetOptions',
    async ({ ownerType = 'doctor', doctorId = null } = {}, { rejectWithValue }) => {
        const axiosInstance = useAxiosAuth();
        try {
            const params = new URLSearchParams({ purpose: 'manage' });
            if (ownerType === 'doctor' && doctorId) params.set('doctorId', doctorId);
            const res = await axiosInstance.get(`${baseURL}/check-sets?${params.toString()}`);
            const data = res.data || {};
            return {
                mine: ownerType === 'doctor' ? [...(data.mine || [])].sort(byName) : [],
                hospital: [...(data.hospital || [])].sort(byName),
            };
        } catch (error) {
            return rejectWithValue(serializeError(error));
        }
    }
);

// What may be started (purpose=start): a doctor's own + the hospital's. The
// admin names the doctor it will run under (without one, hospital only).
export const fetchStartOptions = createAsyncThunk(
    'programmes/fetchStartOptions',
    async ({ doctorId = null } = {}, { rejectWithValue }) => {
        const axiosInstance = useAxiosAuth();
        try {
            const params = new URLSearchParams({ purpose: 'start' });
            if (doctorId) params.set('doctorId', doctorId);
            const res = await axiosInstance.get(`${baseURL}/care-programmes?${params.toString()}`);
            return res.data || {};
        } catch (error) {
            return rejectWithValue(serializeError(error));
        }
    }
);

/* ---------------------------------------------------------------------------
 * A patient's programmes (hospital-internal)
 * ------------------------------------------------------------------------- */

export const fetchPatientProgrammes = createAsyncThunk(
    'programmes/fetchPatient',
    async ({ patientId, status = 'all' }, { rejectWithValue }) => {
        const axiosInstance = useAxiosAuth();
        try {
            const res = await axiosInstance.get(`${baseURL}/patient-programmes/patient/${enc(patientId)}?status=${enc(status)}`);
            return res.data || {};
        } catch (error) {
            return rejectWithValue(serializeError(error));
        }
    },
    {
        condition: ({ patientId } = {}) => Boolean(patientId),
    }
);

export const startPatientProgramme = createAsyncThunk(
    'programmes/start',
    async ({ patientId, programmeId, startDate, appointmentId = null, doctorId = null }, { rejectWithValue }) => {
        const axiosInstance = useAxiosAuth();
        try {
            const body = { patientId, programmeId, startDate };
            if (appointmentId) body.appointmentId = appointmentId;
            if (doctorId) body.doctorId = doctorId;
            const res = await axiosInstance.post(`${baseURL}/patient-programmes`, body);
            return res.data || {};
        } catch (error) {
            return rejectWithValue(serializeError(error));
        }
    }
);

export const tickMilestone = createAsyncThunk(
    'programmes/tick',
    // `patientId` (in the arg) names the list the answer goes back into.
    async ({ patientProgrammeId, key, done, note }, { rejectWithValue }) => {
        const axiosInstance = useAxiosAuth();
        try {
            const body = { done: done === true };
            if (done === true && note !== undefined && note !== null && String(note).trim() !== '') body.note = String(note).trim();
            const res = await axiosInstance.put(`${baseURL}/patient-programmes/${enc(patientProgrammeId)}/milestones/${enc(key)}`, body);
            return res.data || {};
        } catch (error) {
            return rejectWithValue(serializeError(error));
        }
    }
);

export const closePatientProgramme = createAsyncThunk(
    'programmes/close',
    // `patientId` (in the arg) names the list the answer goes back into.
    async ({ patientProgrammeId, outcome, addLifeEvent = false, title = null, occurredAt = null, appointmentId = null }, { rejectWithValue }) => {
        const axiosInstance = useAxiosAuth();
        try {
            const body = { outcome, addLifeEvent: addLifeEvent === true };
            if (addLifeEvent === true) {
                if (title) body.title = title;
                if (occurredAt) body.occurredAt = occurredAt;
                if (appointmentId) body.appointmentId = appointmentId;
            }
            const res = await axiosInstance.post(`${baseURL}/patient-programmes/${enc(patientProgrammeId)}/close`, body);
            return res.data || {};
        } catch (error) {
            return rejectWithValue(serializeError(error));
        }
    }
);

/* ---------------------------------------------------------------------------
 * The line for booking (doc/programme-visits.md, "The line for booking")
 * ------------------------------------------------------------------------- */

// GET /patient-programmes/booking-line?patientId=&doctorId= → { programmes: [Line] }
// — the ACTIVE programmes of this patient run by this doctor at this hospital.
// An empty list means "ask nothing": the booking is unchanged. Kept out of the
// store: the booking form asks once per (patient, doctor) and holds the answer.
export const fetchBookingLine = createAsyncThunk(
    'programmes/bookingLine',
    async ({ patientId, doctorId }, { rejectWithValue }) => {
        const axiosInstance = useAxiosAuth();
        try {
            const params = new URLSearchParams({ patientId, doctorId });
            const res = await axiosInstance.get(`${baseURL}/patient-programmes/booking-line?${params.toString()}`);
            const list = res.data?.programmes;
            return { programmes: Array.isArray(list) ? list : [] };
        } catch (error) {
            return rejectWithValue(serializeError(error));
        }
    },
    {
        condition: ({ patientId, doctorId } = {}) => Boolean(patientId && doctorId),
    }
);

/* ---------------------------------------------------------------------------
 * State
 * ------------------------------------------------------------------------- */

const emptyManage = () => ({
    doctorId: null,
    mine: [],
    hospital: [],
    loading: false,
    loaded: false,
    error: null,
});

const emptyOptions = () => ({ scope: null, mine: [], hospital: [], loading: false, loaded: false, error: null });

const replaceIn = (list, programme) => {
    const at = list.findIndex((p) => p.programmeId === programme.programmeId);
    if (at >= 0) list[at] = programme;
    else list.push(programme);
    list.sort(byName);
};

// `manage` holds ONE list at a time: the doctor's own (asked without a
// doctorId), the hospital's, or — for the admin — the doctor picked on the
// hospital page. An answer for a list no longer on screen must not overwrite
// the one that is.
const sameManage = (manage, arg) => manage.doctorId === (arg?.doctorId || null);
const mineFits = (manage, programme) => manage.doctorId === null || manage.doctorId === programme.doctorId;

const optionScope = ({ ownerType = 'doctor', doctorId = null } = {}) => `${ownerType}:${ownerType === 'doctor' ? (doctorId || '') : ''}`;
const startScope = ({ doctorId = null } = {}) => doctorId || '';

// A write's answer back into the patient's lists: replaced where it is, or
// put first (active = newest start first, closed = newest close first). A
// programme that was just closed moves from `active` to `closed`.
const upsertPatientProgramme = (entry, programme) => {
    if (!entry || !programme?.patientProgrammeId) return;
    const id = programme.patientProgrammeId;
    const into = programme.status === 'closed' ? 'closed' : 'active';
    const other = into === 'closed' ? 'active' : 'closed';
    entry[other] = (entry[other] || []).filter((p) => p.patientProgrammeId !== id);
    const list = entry[into] || [];
    const at = list.findIndex((p) => p.patientProgrammeId === id);
    if (at >= 0) list[at] = programme;
    else list.unshift(programme);
    entry[into] = list;
};

const programmeSlice = createSlice({
    name: 'programmes',
    initialState: {
        manage: emptyManage(),
        saving: false,
        // The template a delete / copy is running on (its button spins).
        busyId: null,
        setOptions: emptyOptions(),
        startOptions: emptyOptions(),
        // patientId → { scope, active, closed, loading, loaded, error }
        patients: {},
        // The patient programme (or `start`) a write is running on.
        acting: null,
    },
    reducers: {
        clearPatientProgrammes(state, action) {
            const id = action?.payload;
            if (id) delete state.patients[id];
            else state.patients = {};
        },
    },
    extraReducers: (builder) => {
        builder
            .addCase(fetchProgrammes.pending, (state, action) => {
                const doctorId = action.meta.arg?.doctorId || null;
                if (state.manage.doctorId !== doctorId) state.manage = { ...emptyManage(), doctorId };
                state.manage.loading = true;
                state.manage.error = null;
            })
            .addCase(fetchProgrammes.fulfilled, (state, action) => {
                if (!sameManage(state.manage, action.meta.arg)) return;
                const p = action.payload || {};
                state.manage.loading = false;
                state.manage.loaded = true;
                state.manage.mine = [...(p.mine || [])].sort(byName);
                state.manage.hospital = [...(p.hospital || [])].sort(byName);
            })
            .addCase(fetchProgrammes.rejected, (state, action) => {
                if (!sameManage(state.manage, action.meta.arg)) return;
                state.manage.loading = false;
                state.manage.loaded = true;
                state.manage.error = action.payload || { code: null, message: 'Request failed' };
            })
            .addCase(createProgramme.pending, (state) => { state.saving = true; })
            .addCase(createProgramme.fulfilled, (state, action) => {
                state.saving = false;
                const p = action.payload?.programme;
                if (!p) return;
                if (p.ownerType === 'tenant') replaceIn(state.manage.hospital, p);
                else if (mineFits(state.manage, p)) replaceIn(state.manage.mine, p);
            })
            .addCase(createProgramme.rejected, (state) => { state.saving = false; })
            .addCase(updateProgramme.pending, (state) => { state.saving = true; })
            .addCase(updateProgramme.fulfilled, (state, action) => {
                state.saving = false;
                const p = action.payload?.programme;
                if (!p) return;
                if (p.ownerType === 'tenant') replaceIn(state.manage.hospital, p);
                else if (mineFits(state.manage, p)) replaceIn(state.manage.mine, p);
            })
            .addCase(updateProgramme.rejected, (state) => { state.saving = false; })
            .addCase(deleteProgramme.pending, (state, action) => { state.busyId = action.meta.arg.programmeId; })
            .addCase(deleteProgramme.fulfilled, (state, action) => {
                state.busyId = null;
                const { programmeId } = action.payload;
                state.manage.mine = state.manage.mine.filter((p) => p.programmeId !== programmeId);
                state.manage.hospital = state.manage.hospital.filter((p) => p.programmeId !== programmeId);
            })
            .addCase(deleteProgramme.rejected, (state) => { state.busyId = null; })
            .addCase(copyProgramme.pending, (state, action) => { state.busyId = action.meta.arg.programmeId; })
            .addCase(copyProgramme.fulfilled, (state, action) => {
                state.busyId = null;
                const p = action.payload?.programme;
                if (p && mineFits(state.manage, p)) replaceIn(state.manage.mine, p);
            })
            .addCase(copyProgramme.rejected, (state) => { state.busyId = null; })
            .addCase(fetchProgrammeSetOptions.pending, (state, action) => {
                const scope = optionScope(action.meta.arg);
                if (state.setOptions.scope !== scope) state.setOptions = { ...emptyOptions(), scope };
                state.setOptions.loading = true;
                state.setOptions.error = null;
            })
            .addCase(fetchProgrammeSetOptions.fulfilled, (state, action) => {
                if (state.setOptions.scope !== optionScope(action.meta.arg)) return;
                state.setOptions.loading = false;
                state.setOptions.loaded = true;
                state.setOptions.mine = action.payload.mine;
                state.setOptions.hospital = action.payload.hospital;
            })
            .addCase(fetchProgrammeSetOptions.rejected, (state, action) => {
                if (state.setOptions.scope !== optionScope(action.meta.arg)) return;
                state.setOptions.loading = false;
                state.setOptions.loaded = true;
                state.setOptions.error = action.payload || { code: null };
            })
            .addCase(fetchStartOptions.pending, (state, action) => {
                const scope = startScope(action.meta.arg);
                if (state.startOptions.scope !== scope) state.startOptions = { ...emptyOptions(), scope };
                state.startOptions.loading = true;
                state.startOptions.error = null;
            })
            .addCase(fetchStartOptions.fulfilled, (state, action) => {
                if (state.startOptions.scope !== startScope(action.meta.arg)) return;
                const p = action.payload || {};
                state.startOptions.loading = false;
                state.startOptions.loaded = true;
                state.startOptions.mine = [...(p.mine || [])].sort(byName);
                state.startOptions.hospital = [...(p.hospital || [])].sort(byName);
            })
            .addCase(fetchStartOptions.rejected, (state, action) => {
                if (state.startOptions.scope !== startScope(action.meta.arg)) return;
                state.startOptions.loading = false;
                state.startOptions.loaded = true;
                state.startOptions.error = action.payload || { code: null };
            })
            .addCase(fetchPatientProgrammes.pending, (state, action) => {
                const { patientId } = action.meta.arg;
                const prev = state.patients[patientId];
                // A re-read keeps what is on screen until the answer lands.
                state.patients[patientId] = {
                    scope: prev?.scope || null,
                    active: prev?.active || [],
                    closed: prev?.closed || [],
                    loaded: prev?.loaded || false,
                    loading: true,
                    error: null,
                };
            })
            .addCase(fetchPatientProgrammes.fulfilled, (state, action) => {
                const { patientId } = action.meta.arg;
                const p = action.payload || {};
                state.patients[patientId] = {
                    scope: p.scope || null,
                    active: Array.isArray(p.active) ? p.active : [],
                    closed: Array.isArray(p.closed) ? p.closed : [],
                    loaded: true,
                    loading: false,
                    error: null,
                };
            })
            .addCase(fetchPatientProgrammes.rejected, (state, action) => {
                const { patientId } = action.meta.arg;
                state.patients[patientId] = {
                    scope: null,
                    active: [],
                    closed: [],
                    loaded: true,
                    loading: false,
                    error: action.payload || { code: null },
                };
            })
            .addCase(startPatientProgramme.pending, (state) => { state.acting = 'start'; })
            .addCase(startPatientProgramme.fulfilled, (state, action) => {
                state.acting = null;
                const entry = state.patients[action.meta.arg.patientId];
                upsertPatientProgramme(entry, action.payload?.programme);
            })
            .addCase(startPatientProgramme.rejected, (state) => { state.acting = null; })
            .addCase(tickMilestone.pending, (state, action) => { state.acting = action.meta.arg.patientProgrammeId; })
            .addCase(tickMilestone.fulfilled, (state, action) => {
                state.acting = null;
                upsertPatientProgramme(state.patients[action.meta.arg.patientId], action.payload?.programme);
            })
            .addCase(tickMilestone.rejected, (state) => { state.acting = null; })
            .addCase(closePatientProgramme.pending, (state, action) => { state.acting = action.meta.arg.patientProgrammeId; })
            .addCase(closePatientProgramme.fulfilled, (state, action) => {
                state.acting = null;
                upsertPatientProgramme(state.patients[action.meta.arg.patientId], action.payload?.programme);
            })
            .addCase(closePatientProgramme.rejected, (state) => { state.acting = null; });
    },
});

export const { clearPatientProgrammes } = programmeSlice.actions;
export default programmeSlice.reducer;
